import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, timeout } from 'rxjs/operators';
import { EntitlementsService } from '@app/core/services/entitlements/entitlements.service';
import { SubscriptionService } from '@app/pages/dashboard/components/subscription-modal/services/subscription.service';
import * as EntitlementsActions from '../actions/entitlements.actions';
import { EntitlementResponse, EntitlementsError, Subscription } from '../types/entitlements.types';

const SUBSCRIPTION_ACTION_TIMEOUT_MS = 30_000;

function toEntitlementsError(error: any): EntitlementsError {
  return {
    message: error?.error?.message || error?.message || '',
    statusCode: error?.status ?? error?.statusCode,
  };
}

@Injectable()
export class EntitlementsEffects {
  loadEntitlements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntitlementsActions.loadEntitlements),
      switchMap(() =>
        this.entitlementsService.getEntitlementsDashboard().pipe(
          map((response: EntitlementResponse) => {
            if (response.error) {
              return EntitlementsActions.loadEntitlementsFailure({
                error: { message: response.msg || 'Failed to load entitlements' },
              });
            }

            return EntitlementsActions.loadEntitlementsSuccess({ entitlements: response });
          }),
          catchError(error =>
            of(EntitlementsActions.loadEntitlementsFailure({ error: toEntitlementsError(error) }))
          )
        )
      )
    )
  );

  scheduleCancelSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntitlementsActions.scheduleCancelSubscription),
      switchMap(({ subscriptionId }) =>
        this.subscriptionService.scheduleCancel(subscriptionId).pipe(
          timeout(SUBSCRIPTION_ACTION_TIMEOUT_MS),
          map((response: { data?: Subscription } | Subscription) => {
            const subscription: Subscription = (response as { data?: Subscription })?.data ?? (response as Subscription);

            return EntitlementsActions.scheduleCancelSubscriptionSuccess({ subscription });
          }),
          catchError(error =>
            of(
              EntitlementsActions.scheduleCancelSubscriptionFailure({
                error: toEntitlementsError(error),
              })
            )
          )
        )
      )
    )
  );

  reactivateSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EntitlementsActions.reactivateSubscription),
      switchMap(({ subscriptionId }) =>
        this.subscriptionService.reactivate(subscriptionId).pipe(
          timeout(SUBSCRIPTION_ACTION_TIMEOUT_MS),
          map((response: { data?: Subscription } | Subscription) => {
            const subscription: Subscription = (response as { data?: Subscription })?.data ?? (response as Subscription);

            return EntitlementsActions.reactivateSubscriptionSuccess({ subscription });
          }),
          catchError(error =>
            of(
              EntitlementsActions.reactivateSubscriptionFailure({
                error: toEntitlementsError(error),
              })
            )
          )
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private entitlementsService: EntitlementsService,
    private subscriptionService: SubscriptionService
  ) {}
}
